
import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { ResourceData } from '@/types/slimeHabit';
import { usePersistentStorage } from '@/hooks/usePersistentStorage';
import { toast } from '@/hooks/use-toast';

interface ResourceContextType {
  resources: ResourceData;
  setResources: (resources: ResourceData) => void;
  updateResources: (resources: Partial<ResourceData>) => void;
  addResource: (type: keyof ResourceData, amount: number) => void;
  canAfford: (cost: Partial<ResourceData>) => boolean;
  spendResources: (cost: Partial<ResourceData>) => boolean;
  isLoading: boolean;
}

const ResourceContext = createContext<ResourceContextType | undefined>(undefined);

export const useResources = (): ResourceContextType => {
  const context = useContext(ResourceContext);
  if (!context) {
    throw new Error('useResources must be used within a ResourceProvider');
  }
  return context;
};

interface ResourceProviderProps {
  children: ReactNode;
}

const defaultResources: ResourceData = {
  essence: 100,
  mana: 50,
  coins: 25,
  gems: 5,
  luckShards: 2
};

export const ResourceProvider: React.FC<ResourceProviderProps> = ({ children }) => {
  const [resources, setResources, removeResources] = usePersistentStorage<ResourceData>(
    'slimehabit-resources',
    defaultResources
  );
  const [isLoading, setIsLoading] = React.useState(false);

  const updateResources = (newResources: Partial<ResourceData>) => {
    setResources(prev => {
      const updated = { ...prev, ...newResources };
      // 确保资源值不会为负数
      Object.keys(updated).forEach(key => {
        if (updated[key as keyof ResourceData] < 0) {
          updated[key as keyof ResourceData] = 0;
        }
      });
      return updated;
    });
  };

  const addResource = (type: keyof ResourceData, amount: number) => {
    setResources(prev => ({ 
      ...prev, 
      [type]: Math.max(0, prev[type] + amount)
    }));
  };

  const canAfford = (cost: Partial<ResourceData>): boolean => {
    return Object.entries(cost).every(([type, amount]) => {
      const currentAmount = resources[type as keyof ResourceData];
      return currentAmount >= (amount || 0);
    });
  };

  const spendResources = (cost: Partial<ResourceData>): boolean => {
    if (!canAfford(cost)) {
      toast({
        title: "资源不足",
        description: "您没有足够的资源完成此操作",
        variant: "destructive"
      });
      return false;
    }

    const updates: Partial<ResourceData> = {};
    Object.entries(cost).forEach(([type, amount]) => {
      if (amount && amount > 0) {
        updates[type as keyof ResourceData] = resources[type as keyof ResourceData] - amount;
      }
    });

    updateResources(updates);
    return true;
  };

  return (
    <ResourceContext.Provider value={{ 
      resources, 
      setResources, 
      updateResources, 
      addResource, 
      canAfford, 
      spendResources,
      isLoading 
    }}>
      {children}
    </ResourceContext.Provider>
  );
};
